import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Users, Search, Plus } from 'lucide-react';
import { useEmployeesHCM } from '../hooks/useHCM';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { Spinner } from '@travellegacy/nexops-design-system';
import { TabFilter } from '@travellegacy/nexops-design-system';
import type { EmployeeStatus } from '../types';

// Função para formatar status em português
const getStatusLabel = (status: EmployeeStatus): string => {
  const statusLabels: Record<EmployeeStatus, string> = {
    ACTIVE: 'Ativo',
    INACTIVE: 'Inativo',
    VACATION: 'Férias',
  };
  return statusLabels[status];
};

// Função para variante do badge baseada no status
const getStatusVariant = (status: EmployeeStatus): 'success' | 'default' | 'warning' => {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'VACATION':
      return 'warning';
    case 'INACTIVE':
      return 'default';
    default:
      return 'default';
  }
};

export function EmployeesHCMPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<EmployeeStatus>('ACTIVE');

  const { data: employees = [], isLoading } = useEmployeesHCM({
    search: searchQuery,
    status: statusFilter,
  });

  // Filter employees by search query
  const filteredEmployees = useMemo(() => {
    if (!searchQuery) return employees;

    const query = searchQuery.toLowerCase();
    return employees.filter(
      (employee) =>
        employee.user?.name.toLowerCase().includes(query) ||
        employee.user?.email.toLowerCase().includes(query)
    );
  }, [employees, searchQuery]);

  // Calculate counts for tabs
  const statusCounts = useMemo(() => {
    // We need to fetch all employees to get counts
    // For now, we'll just show the current filtered count
    return {
      ACTIVE: statusFilter === 'ACTIVE' ? filteredEmployees.length : 0,
      INACTIVE: statusFilter === 'INACTIVE' ? filteredEmployees.length : 0,
      VACATION: statusFilter === 'VACATION' ? filteredEmployees.length : 0,
    };
  }, [filteredEmployees.length, statusFilter]);

  // Reset search when status changes
  const handleStatusFilterChange = (newFilter: EmployeeStatus) => {
    setStatusFilter(newFilter);
  };

  const handleSearchChange = (newSearch: string) => {
    setSearchQuery(newSearch);
  };

  // Tabs configuration (sem a aba "TODOS")
  const statusTabs = [
    { value: 'ACTIVE', label: 'Ativos', count: statusCounts.ACTIVE, color: 'green' as const },
    { value: 'VACATION', label: 'Férias', count: statusCounts.VACATION, color: 'yellow' as const },
    { value: 'INACTIVE', label: 'Inativos', count: statusCounts.INACTIVE, color: 'gray' as const },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Colaboradores</h1>
          <p className="text-muted-foreground">Gestão de Capital Humano</p>
        </div>
        <Link to="/people-core/hcm/employees/new">
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Novo Colaborador
          </Button>
        </Link>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              <span>Lista de Colaboradores</span>
            </CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar por nome ou email..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Status Tabs */}
          <TabFilter
            tabs={statusTabs}
            currentTab={statusFilter}
            onChange={(value) => handleStatusFilterChange(value as EmployeeStatus)}
          />
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          ) : filteredEmployees.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              {searchQuery
                ? 'Nenhum colaborador encontrado com os filtros aplicados'
                : 'Nenhum colaborador encontrado'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-semibold text-sm">Nome</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm">Email</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm">Contrato</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm">Departamento</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm">Cargo</th>
                    <th className="text-right py-3 px-4 font-semibold text-sm">Salário</th>
                    <th className="text-right py-3 px-4 font-semibold text-sm">Benefícios</th>
                    <th className="text-right py-3 px-4 font-semibold text-sm">Custo Total</th>
                    <th className="text-center py-3 px-4 font-semibold text-sm">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.map((employee) => {
                    const currentSalary = employee.currentSalary?.employeeSalary || employee.currentSalary?.value;
                    // Custo base (sem comissões): PJ = salário; CLT = salário + FGTS 8% sobre o salário
                    const baseSalaryCost = (currentSalary || 0) * (employee.contractType === 'CLT' ? 1.08 : 1);

                    // Calcular benefícios totais
                    const benefitVAVR = employee.benefitVAVR || 0;
                    const benefitHomeOffice = employee.benefitHomeOffice || 0;
                    const benefitVT = employee.benefitVT || 0;
                    const totalBenefits = benefitVAVR + benefitHomeOffice + benefitVT;

                    // Calcular descontos
                    const vtDiscount = employee.benefitVTDiscount || 0;

                    // Custo total para a empresa: salário com encargos + benefícios - desconto VT
                    // (o INSS do colaborador sai do salário bruto, mas a empresa recolhe a guia: continua sendo custo)
                    const totalCost = (baseSalaryCost || 0) + totalBenefits - vtDiscount;

                    const status = employee.status || 'INACTIVE';

                    return (
                      <tr
                        key={employee.id}
                        className="border-b hover:bg-accent/50 transition-colors"
                      >
                        <td className="py-3 px-4">
                          <Link
                            to={`/people-core/hcm/employees/${employee.id}`}
                            className="font-medium hover:text-primary hover:underline"
                          >
                            {employee.user?.name}
                          </Link>
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">
                          {employee.user?.email || '-'}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {employee.contractType ? (
                            <span
                              className={`text-xs px-2 py-1 rounded font-medium ${
                                employee.contractType === 'CLT'
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                  : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                              }`}
                            >
                              {employee.contractType}
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {employee.jobPosition?.department?.name || '-'}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {employee.jobPosition?.name || '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-right font-medium">
                          {currentSalary
                            ? `R$ ${currentSalary.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}`
                            : '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-right font-medium text-green-600 dark:text-green-400">
                          {totalBenefits > 0
                            ? `R$ ${totalBenefits.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}`
                            : '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-right font-medium text-red-600 dark:text-red-400">
                          {totalCost > 0
                            ? `R$ ${totalCost.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}`
                            : '-'}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge variant={getStatusVariant(status)}>
                            {getStatusLabel(status)}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
